import { FC, ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { clearAktivitetState, getAktivitet } from 'store/aktivitetSlice';
import { clearBebyggelseobjekt } from 'store/bebyggelseobjektSlice';
import { clearBebyggelseobjektUtkast } from 'store/bebyggelseobjektUtkastSlice';
import { setMapMode } from 'store/mapSlice';
import {
  clearPubliceradeBebyggelseobjekt,
  getPubliceradeBebyggelseobjekt
} from 'store/publiceradeBebyggelseobjektSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';

import InventeringPresentation from 'components/aktivitetPresentation//inventering/InventeringPresentation';
import EnkelinventeringPresentation from 'components/aktivitetPresentation/enkelinventering/EnkelinventeringPresentation';
import KulturvardeForstortPresentation from 'components/aktivitetPresentation/kulturvardeForstort/KulturvardeForstortPresentation';
import MigreringPresentation from 'components/aktivitetPresentation/migrering/MigreringPresentation';
import SkyddsklassningPresentation from 'components/aktivitetPresentation/skyddsklassning/SkyddsklassningPresentation';
import { DrawerContainer, NavigationDrawerHead, SpinnerContainer } from 'components/drawerSections/DrawerSections';
import { activeSource } from 'components/map/Layers';
import { Colors } from 'components/map/Styles';
import { inventeringCircle } from 'components/map/icons';

import { Aktivitet, AktivitetState } from 'models';

import { AktivitetstypEnum, MapModeEnum } from 'enums';

import {
  isEnkelinventering,
  isInventering,
  isKulturvardeForstort,
  isMigreringsaktivitet,
  isSkyddsklassningsaktivitet
} from 'utilities/typeGuards';

const AktivitetPresentation: FC = (): ReactNode => {
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const { data: aktivitetData, requestStatus } = useAppSelector((state: RootState) => state.aktivitet);

  const [aktivitet, setAktivitet] = useState<Aktivitet | undefined>();

  const isLoading = requestStatus === 'pending';

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    if (aktivitetData) {
      const aktivitet = (
        Object.entries(aktivitetData) as [keyof AktivitetState, AktivitetState[keyof AktivitetState]][]
      ).find(([key, value]) => key !== ('eTag' as keyof AktivitetState) && value !== undefined)?.[1] as Aktivitet;

      if (aktivitet) {
        setAktivitet(aktivitet);
      }
    }
  }, [aktivitetData]);

  useEffect(() => {
    //Cleanup
    return () => {
      setDrawerMobileTemplate(undefined);
      dispatch(clearAktivitetState());
      dispatch(clearBebyggelseobjekt());
      dispatch(clearBebyggelseobjektUtkast());
      dispatch(clearPubliceradeBebyggelseobjekt());
      activeSource.clear();
      activeSource.changed();
      dispatch(setMapMode(MapModeEnum.DEFAULT));
    };
  }, [dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    if (id) {
      dispatch(getAktivitet(id));
      dispatch(getPubliceradeBebyggelseobjekt(id));
    } else {
      dispatch(clearPubliceradeBebyggelseobjekt());
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (aktivitet) {
      if (
        isEnkelinventering(aktivitet) ||
        isKulturvardeForstort(aktivitet) ||
        isMigreringsaktivitet(aktivitet) ||
        isSkyddsklassningsaktivitet(aktivitet)
      ) {
        setDrawerMobileTemplate(
          <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
            <h2 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">
              {isSkyddsklassningsaktivitet(aktivitet) ? aktivitet.skyddsklassningstyp_namn : aktivitet?.aktivitetsnamn}
            </h2>
            <div className="tw-flex tw-items-center tw-justify-start">
              <p>Aktivitetsnummer: {aktivitet?.aktivitetsnummer}</p>
            </div>
          </div>
        );
      }

      if (isInventering(aktivitet)) {
        setDrawerMobileTemplate(
          <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
            <h2 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">{aktivitet?.titel || ''}</h2>
            <div className="tw-flex tw-items-center tw-justify-start">
              <img
                className="tw-mr-2"
                src={'data:image/svg+xml,' + encodeURIComponent(inventeringCircle(Colors.RAA_BLUE, Colors.WHITE))}
                alt="Ikon för inventering"
              />
              <p>Inventering {aktivitet ? aktivitet.aktivitetsnummer : '(ej sparad)'}</p>
            </div>
          </div>
        );
      }
    }
  }, [aktivitet, setDrawerMobileTemplate]);

  return (
    <SpinnerContainer showSpinner={isLoading}>
      <DrawerContainer>
        <NavigationDrawerHead
          disableBackNavigation={aktivitet?.aktivitetstyp === AktivitetstypEnum.SKYDDSKLASSNING || false}
        />

        {aktivitet && isInventering(aktivitet) && <InventeringPresentation />}
        {aktivitet && isEnkelinventering(aktivitet) && <EnkelinventeringPresentation />}
        {aktivitet && isMigreringsaktivitet(aktivitet) && <MigreringPresentation />}
        {aktivitet && isSkyddsklassningsaktivitet(aktivitet) && <SkyddsklassningPresentation />}
        {aktivitet && isKulturvardeForstort(aktivitet) && <KulturvardeForstortPresentation />}
      </DrawerContainer>
    </SpinnerContainer>
  );
};

export default AktivitetPresentation;
